
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { map } from 'rxjs/operators';
import { ISurfaceDevelopmentScheduleActivityDto, SurfaceDevelopmentScheduleActivityDto } from '../../schedule/app-schedule-task-surfacedev/app-schedule-task-surfacedev.dtos';

export class LookupResults<t> {
  public Message: string;
  public Success: boolean;
  public Result: t[];
}

export class GetScheduleResults {
  public Message: string;
  public Success: boolean;
  public Result: {
    ScheduleActivities: ISurfaceDevelopmentScheduleActivityDto[],
    UserEditedPhases: string[]
  }
}

export class LookupResult<t> {
  public Message: string;
  public Success: boolean;
  public Result: t;
}

@Injectable({
  providedIn: 'root',
})
export class LookupPlusService {
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  private BASE_URL = this.environment.apiUrl;
  _Cache: Map<string, any> = new Map();

  public getLookupCustom(oString: string): Promise<any> {
    let url = `${this.BASE_URL}${oString}`;

    return this.http
      .get(url)
      .toPromise()
      .then(response => response);
  }

  public getLookup(_table: string, _oString, useCache: boolean = false): Promise<any> {
    let plusQuery: string = _oString;
    if (plusQuery.charAt(0) == '?') {
      //get rid of first ?
      plusQuery = plusQuery.substring(1);
    }
    let url = `${this.BASE_URL}${_table}?${plusQuery}&%24count=true`;

    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    return this.http
      .get(url)
      .toPromise()
      .then(response => {
        if (useCache) {
          this._Cache.set(url, response);
        }
        return response;
      });
  }

  public async getTypedLookup<T>(table: string, query = '', includeCount: boolean = true, useCache: boolean = false): Promise<GridDataResultTyped<T>> {
    if (query && query.substr(0, 1) !== '?') {
      query = '?' + query;
    }
    if (query && includeCount) {
      query += '&$count=true';
    } else if (includeCount) {
      query += '?count=true';
    }
    let url = `${this.BASE_URL}${table}${query}`;

    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    return this.http
      .get(url)
      .toPromise()
      .then(response => {
        var result = new GridDataResultTyped<T>(response);
        this._Cache.set(url, result);
        return result;
      });
  }

  public async getLookupNoCount(_table: string, query: string = null, useCache: boolean = false): Promise<any> {

    let url = `${this.BASE_URL}${_table}`;
    if (query) {
      url += '?' + query;
    }

    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    return this.http
      .get(url)
      .toPromise()
      .then(response => {
        if (useCache) {
          this._Cache.set(_table, response);
        }
        return response;
      });
  }

  public async getLookupCountAsync<t>(_table: string, useCache: boolean = false): Promise<GridDataResultTyped<t>> {
    let url = `${this.BASE_URL}${_table}`;
    if (url.includes('?')) {
      url += '&%24count=true';
    } else {
      url += '?%24count=true';
    }
    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    return this.http
      .get(url).pipe(
        map(r => new GridDataResultTyped<t>(r)))
      .toPromise()
      .then(response => {
        if (useCache) {
          this._Cache.set(_table, response);
        }
        return response;
      });
  }

  public async getLookupResultNoCountAsync(
    _table: string,
    query: string = null,
    useCache: boolean = false)
    : Promise<GetScheduleResults> {

    let url = `${this.BASE_URL}${_table}`;
    if (query) {
      url += '?' + query;
    }
    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    let result = await this.http
      .get<GetScheduleResults>(url)
      .pipe(
        map((response: GetScheduleResults) => {
          const final: GetScheduleResults = new GetScheduleResults();
          final.Message = response.Message;
          final.Success = response.Success;

          final.Result = {
            ScheduleActivities: [],
            UserEditedPhases: []
          };

          if (response.Result && response.Result.ScheduleActivities && response.Result.ScheduleActivities.length) {
            final.Result.ScheduleActivities = response.Result.ScheduleActivities.map((item: ISurfaceDevelopmentScheduleActivityDto) => {
              return new SurfaceDevelopmentScheduleActivityDto(item);
            })
          } else {
            final.Result.ScheduleActivities = [];
          }

          if (response.Result) {
            final.Result.UserEditedPhases = response.Result.UserEditedPhases;
          }

          return final;
        })
      )
      .toPromise()
      .then(response => {
        if (useCache) {
          this._Cache.set(_table, response);
        }
        return response;
      }
      );

    return result;
  }

  public async getLookupNoCountAsync<t, n>(
    _table: string,
    query: string = null,
    useCache: boolean = false,
    mapFunction: (item: t) => n)
    : Promise<n[]> {

    let url = `${this.BASE_URL}${_table}`;
    if (query) {
      url += '?' + query;
    }
    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    let result = await this.http
      .get<t[]>(url)
      .pipe(
        map((response: t[]) => {

          return response.map((item: t) => {
            return mapFunction(item);
          })
        })
      )
      .toPromise()
      .then(response => {
        if (useCache) {
          this._Cache.set(_table, response);
        }
        return response;
      }
      );

    return result;
  }

  public async getSingleTableRow(request: string): Promise<any> {
    let url = `${this.BASE_URL}${request}`;
    try {
      return await this.http
        .get(url)
        .toPromise();
    }
    catch (error) {
      console.error("Error in getSingleTableRow", error);
      throw error;
    }
  }

  public async getSingleTableRowTyped<T>(request: string): Promise<T> {
    let url = `${this.BASE_URL}${request}`;
    try {
      return await this.http
        .get<T>(url)
        .toPromise();
    }
    catch (error) {
      console.error("Error in getSingleTableRowTyped", error);
      throw error;
    }
  }

  public async getTypedObject<T>(command: string, endpoint: any): Promise<T> {
    let url = `${this.BASE_URL}${command}/${endpoint}`;

    return await this.http
      .get<T>(url)
      .toPromise();
  }

  public async getObject(command: string, endpoint: any): Promise<any> {
    let url = `${this.BASE_URL}${command}/${endpoint}`;

    return await this.http
      .get(url)
      .toPromise();
  }

  public async getById(table: string, id: number): Promise<any> {
    let url = `${this.BASE_URL}${table}(${id})`;
    return await this.http
      .get(url)
      .toPromise();
  }
}

export class GridDataResultTyped<T> implements GridDataResult {
  data: T[];
  total: number;
  constructor(source: any) {
    this.data = source.value as T[];
    this.total = parseInt(source['@odata.count'], 10);
  }
}